// app/(tabs)/market/creer-boutique.tsx
//
// Écran dédié à la création d'une boutique (userpro). Atteint depuis
// reglages.tsx → "Mes boutiques" → bouton "Créer une nouvelle boutique"
// (navigation push classique, pas de modale).
//
// Formulaire minimal : nom uniquement — le slug et le timezone sont
// entièrement gérés côté useCreateShop/backend (voir
// hooks/market/useCreateShop.ts et backend/handleMarket.ts → createShop).
//
// Après création réussie, navigue directement vers l'écran de gestion
// de la boutique fraîchement créée : market/gerer/[shopId].tsx
// (router.replace, pas .push, pour ne pas laisser ce formulaire dans
// la pile de navigation).
//
// PATCH i18n : tous les textes affichés à l'utilisateur passent par
// react-i18next (namespace market.creerBoutique / market.common).

import React, { useState, useCallback } from 'react';
import {
  StyleSheet,
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  KeyboardAvoidingView,
  ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';

import { useCreateShop } from '@/hooks/market/useCreateShop';

const NATIVE = Platform.OS !== 'web';

export default function CreateShopScreen() {
  const router = useRouter();
  const { t } = useTranslation();
  const { createShop, loading: creatingShop, error: createShopError } = useCreateShop();

  const [shopName, setShopName] = useState('');

  const handleBack = useCallback(() => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    router.replace('/(tabs)/market/reglages');
  }, [router]);

  const handleCreateShop = useCallback(async () => {
    const trimmedName = shopName.trim();
    if (!trimmedName || creatingShop) return;

    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);

    const shop = await createShop(trimmedName);
    if (shop) {
      router.replace(`/(tabs)/market/gerer/${shop.id}`);
    }
  }, [shopName, creatingShop, createShop, router]);

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      {/* ── Header avec bouton retour ── */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.backBtn} activeOpacity={0.7} onPress={handleBack}>
          <Ionicons name="arrow-back" size={24} color="#333" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t('market.creerBoutique.headerTitle')}</Text>
        <View style={styles.headerSpacer} />
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.iconBlock}>
          <View style={styles.iconWrap}>
            <Ionicons name="storefront" size={32} color="#6610B1" />
          </View>
          <Text style={styles.subtitle}>
            {t('market.creerBoutique.subtitle')}
          </Text>
        </View>

        <View style={styles.formBlock}>
          <Text style={styles.label}>{t('market.creerBoutique.nameLabel')}</Text>
          <TextInput
            style={styles.input}
            placeholder={t('market.creerBoutique.namePlaceholder')}
            placeholderTextColor="#B0A8C4"
            value={shopName}
            onChangeText={setShopName}
            autoFocus
            editable={!creatingShop}
            maxLength={80}
            returnKeyType="done"
            onSubmitEditing={handleCreateShop}
          />

          {!!createShopError && (
            <Text style={styles.errorText}>
              {createShopError === 'session_expired'
                ? t('market.common.sessionExpiredReconnect2')
                : createShopError === 'network_error'
                ? t('market.common.networkError')
                : createShopError}
            </Text>
          )}
        </View>
      </ScrollView>

      {/* ── Action de création, fixée en bas ── */}
      <View style={styles.footer}>
        <TouchableOpacity
          style={[
            styles.confirmBtn,
            (!shopName.trim() || creatingShop) && styles.confirmBtnDisabled,
          ]}
          activeOpacity={0.8}
          onPress={handleCreateShop}
          disabled={!shopName.trim() || creatingShop}
        >
          {creatingShop ? (
            <ActivityIndicator size="small" color="#fff" />
          ) : (
            <Text style={styles.confirmBtnText}>{t('market.creerBoutique.submit')}</Text>
          )}
        </TouchableOpacity>
      </View>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  backBtn: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    flex: 1,
    fontSize: 16,
    fontWeight: '700',
    color: '#222',
    textAlign: 'center',
  },
  headerSpacer: {
    width: 40,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 20,
    paddingTop: 24,
  },
  iconBlock: {
    alignItems: 'center',
    marginBottom: 28,
  },
  iconWrap: {
    width: 64,
    height: 64,
    borderRadius: 18,
    backgroundColor: '#F3E8FF',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 14,
  },
  subtitle: {
    fontSize: 13.5,
    color: '#999',
    textAlign: 'center',
    lineHeight: 19,
    paddingHorizontal: 16,
  },
  formBlock: {
    marginBottom: 20,
  },
  label: {
    fontSize: 13,
    fontWeight: '600',
    color: '#666',
    marginBottom: 8,
  },
  input: {
    backgroundColor: '#F9F5FF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5D9FF',
    paddingHorizontal: 14,
    paddingVertical: Platform.OS === 'ios' ? 14 : 10,
    fontSize: 15,
    color: '#333',
  },
  errorText: {
    fontSize: 12.5,
    color: '#DC2626',
    marginTop: 8,
  },
  footer: {
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: Platform.OS === 'ios' ? 30 : 16,
    borderTopWidth: 1,
    borderTopColor: '#F0F0F0',
  },
  confirmBtn: {
    backgroundColor: '#6610B1',
    borderRadius: 12,
    paddingVertical: 15,
    alignItems: 'center',
    justifyContent: 'center',
  },
  confirmBtnDisabled: {
    backgroundColor: '#C4B5FD',
  },
  confirmBtnText: {
    fontSize: 15,
    fontWeight: '700',
    color: '#fff',
  },
});
